import React, { useEffect, useState } from "react";
import { Button } from "zudoku/components";

const FLAG_KEY = "docs-content-cta";

const SIGNUP_URL =
  "https://portal.zuplo.com/signup?utm_source=docs&utm_medium=content-after";
const DEMO_URL =
  "https://zuplo.com/schedule-call?utm_source=docs&utm_medium=content-after";

type PostHogClient = {
  onFeatureFlags?: (callback: () => void) => (() => void) | undefined;
  getFeatureFlag?: (key: string) => string | boolean | undefined;
  capture?: (event: string, properties?: Record<string, unknown>) => void;
};

const getPostHog = (): PostHogClient | undefined =>
  typeof window !== "undefined"
    ? (window as { posthog?: PostHogClient }).posthog
    : undefined;

const trackClick = (target: "signup" | "demo") => {
  getPostHog()?.capture?.("docs_content_cta_click", {
    cta: "content-after",
    target,
  });
};

/**
 * End-of-content CTA card rendered via the Zudoku `content-after` slot on
 * every docs page. Gated client-side by the PostHog feature flag
 * `docs-content-cta` (experiment): only the "test" variant sees the card,
 * so nothing renders until the experiment is launched.
 */
export const ContentCta = () => {
  const [variant, setVariant] = useState<string | boolean>();

  useEffect(() => {
    const posthog = getPostHog();
    if (!posthog?.onFeatureFlags) return;
    // Re-read window.posthog inside the callback: the snippet stub is
    // replaced by the real posthog-js instance once array.js loads.
    return posthog.onFeatureFlags(() => {
      setVariant(getPostHog()?.getFeatureFlag?.(FLAG_KEY));
    });
  }, []);

  if (variant !== "test") return null;

  return (
    <aside className="my-10 flex flex-col items-start justify-between gap-4 rounded-lg border bg-muted/30 p-6 sm:flex-row sm:items-center">
      <div>
        <p className="font-semibold">
          Deploy a gateway free in under 5 minutes
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          No credit card required. Start with the free plan and scale as you
          grow.
        </p>
      </div>
      <div className="flex shrink-0 gap-2">
        <Button asChild>
          <a href={SIGNUP_URL} onClick={() => trackClick("signup")}>
            Start for Free
          </a>
        </Button>
        <Button variant="outline" asChild>
          <a href={DEMO_URL} onClick={() => trackClick("demo")}>
            Book a demo
          </a>
        </Button>
      </div>
    </aside>
  );
};
